import { MoveDownRight, MoveUpRight } from 'lucide-react';
import { formatAbsoluteGrowth, formatVolume } from 'utils/helpers';
import {
    getDriversSourceConfig,
    normalizeDriversSource,
} from 'utils/driversOfGrowthConfig';
import { timeFormat, timeParse } from 'd3-time-format';
import TikTokTooltipCard from './TikTokTooltipCard';
import dayjs from 'dayjs';
import { useDriversOfGrowth } from 'utils/hooks/useDriversOfGrowth';
import useInstagramThumbnail from 'utils/hooks/useInstagramThumbnail';
import { useMemo } from 'react';
import useTikTokThumbnail from 'utils/hooks/useTikTokThumbnail';

const defaultFormatAndParseTime = (v) =>
    timeFormat('%b %d, %Y')(timeParse('%Y-%m-%d')(v));

const getChangeValue = (accessors, datum) => {
    if (!(accessors && datum)) return null;
    const wowValue = accessors.wowChangeAccessor
        ? accessors.wowChangeAccessor(datum)
        : null;
    if (wowValue != null) return wowValue;

    const momValue = accessors.momChangeAccessor
        ? accessors.momChangeAccessor(datum)
        : null;
    if (momValue != null) return momValue;

    return null;
};

const getPrimaryCount = (accessors, datum) => {
    if (!(accessors && datum)) return 0;
    if (accessors.hashtagsCountAccessor)
        return accessors.hashtagsCountAccessor(datum) ?? 0;
    if (accessors.keywordsCountAccessor)
        return accessors.keywordsCountAccessor(datum) ?? 0;
    return 0;
};

function MiniThumbnailBase({ thumbnailUrl }) {
    return (
        <div className="w-6 h-8 bg-neutral-200 rounded-sm overflow-hidden flex-shrink-0">
            {thumbnailUrl && (
                <img
                    src={thumbnailUrl}
                    alt=""
                    className="w-full h-full object-cover"
                />
            )}
        </div>
    );
}

function MiniThumbnailTikTok({ url }) {
    const { thumbnailUrl } = useTikTokThumbnail(url);
    return <MiniThumbnailBase thumbnailUrl={thumbnailUrl} />;
}

function MiniThumbnailInstagram({ url }) {
    const { thumbnailUrl } = useInstagramThumbnail(url);
    return <MiniThumbnailBase thumbnailUrl={thumbnailUrl} />;
}

function MiniThumbnail({ url, dataSource = 'tiktok' }) {
    const normalizedDataSource = normalizeDriversSource(dataSource);
    const isInstagram =
        normalizedDataSource === 'instagram' ||
        normalizedDataSource === 'pop_index_instagram';
    return isInstagram ? (
        <MiniThumbnailInstagram url={url} />
    ) : (
        <MiniThumbnailTikTok url={url} />
    );
}

const formatEstimatedNote = (sources) => {
    const labels = (sources || []).filter(Boolean);
    if (!labels.length) return null;
    const joined =
        labels.length === 1
            ? labels[0]
            : `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
    return `* ${joined} estimated`;
};

function DriversOfGrowthTooltip({
    left,
    top,
    width = 240,
    chartWidth,
    chartHeight,
    offset = 10,
    accessors,
    tooltipOpen,
    tooltipData,
    formatAndParseTime = defaultFormatAndParseTime,
    metricLabel,
    sourceName,
    dataSource = 'tiktok',
    country,
    category,
    brand,
    dominantSourceImpact = null,
    estimatedSources = [],
}) {
    const normalizedDataSource = normalizeDriversSource(dataSource);
    const sourceConfig = getDriversSourceConfig(normalizedDataSource);
    const estimatedNote = formatEstimatedNote(estimatedSources);
    const resolvedMetricLabel = metricLabel || sourceConfig.metricLabel;
    const resolvedMetricLabelNoWrap = String(
        resolvedMetricLabel || '',
    ).replaceAll(' ', '\u00A0');
    const resolvedSourceName = sourceName || sourceConfig.sourceName;
    const { changeLabel = 'Increase WoW', supportsPosts = true } = sourceConfig;

    const date =
        tooltipData?.datum && accessors?.xAccessor
            ? dayjs(accessors.xAccessor(tooltipData.datum)).format('YYYY-MM-DD')
            : null;

    const hasContext = Boolean(country && (category || brand));

    const hashtagsCount = tooltipData?.datum
        ? getPrimaryCount(accessors, tooltipData.datum)
        : 0;
    const postsCount =
        tooltipData?.datum && accessors?.postsCountAccessor
            ? (accessors.postsCountAccessor(tooltipData.datum) ?? 0)
            : 0;
    const hasDriversData = hashtagsCount > 0 || postsCount > 0;
    const isPopIndexSource = normalizedDataSource.startsWith('pop_index_');
    const shouldShowDriversSection =
        hasContext &&
        (hasDriversData ||
            normalizedDataSource === 'google' ||
            isPopIndexSource);

    const wowChange = tooltipData?.datum
        ? getChangeValue(accessors, tooltipData.datum)
        : null;

    const isNegativeWeek = wowChange != null ? wowChange < 0 : false;

    const driversQuery = useDriversOfGrowth({
        country,
        category,
        brand,
        date,
        enabled: shouldShowDriversSection && !!date && tooltipOpen,
        isNegativeWeek,
        dataSource: normalizedDataSource,
    });

    const hashtags = useMemo(() => {
        if (!driversQuery.hashtagsData) return [];
        return driversQuery.hashtagsData.slice(0, 5).map((item) => item.kw);
    }, [driversQuery.hashtagsData]);

    const posts = useMemo(() => {
        if (!driversQuery.postsData) return [];
        return driversQuery.postsData.slice(0, 5).map((item) => item.url);
    }, [driversQuery.postsData]);

    const isLoading = driversQuery.isLoading && shouldShowDriversSection;
    const hasDominantSourceImpact = dominantSourceImpact?.increase != null;
    const dominantImpactPositive = (dominantSourceImpact?.increase ?? 0) >= 0;
    const dominantMetricLabelNoWrap = String(
        dominantSourceImpact?.metricLabel || '',
    ).replaceAll(' ', '\u00A0');

    if (!tooltipOpen) return null;

    const currentValue = accessors.yAccessor(tooltipData?.datum);
    const isPositive = wowChange != null ? wowChange >= 0 : true;

    return (
        <TikTokTooltipCard
            left={left}
            top={top}
            chartWidth={chartWidth}
            chartHeight={chartHeight}
            offset={offset}
            width={width}
        >
            <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between text-xs text-neutral-700 opacity-80">
                        <span>
                            {tooltipData?.datum
                                ? formatAndParseTime(
                                      accessors.xAccessor(tooltipData?.datum),
                                  )
                                : null}
                        </span>
                        <span>{resolvedSourceName}</span>
                    </div>

                    <div className="flex items-start justify-between gap-2">
                        <span className="text-xs font-medium text-neutral-900 capitalize line-clamp-2 max-w-[120px] min-h-[2lh]">
                            {tooltipData.name}
                        </span>
                        <div className="flex flex-col items-end shrink-0">
                            <span className="text-sm font-semibold text-neutral-900">
                                <span className="uppercase">
                                    {formatVolume(currentValue)}
                                </span>
                                &nbsp;{resolvedMetricLabelNoWrap}
                            </span>
                            {estimatedNote && (
                                <span className="text-[0.5625rem] italic text-neutral-500">
                                    {estimatedNote}
                                </span>
                            )}
                        </div>
                    </div>

                    {wowChange != null && (
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-neutral-700">
                                {changeLabel}
                            </span>
                            <span
                                className={`text-sm font-semibold flex items-center gap-0.5 shrink-0 whitespace-nowrap ${isPositive ? 'text-green-600' : 'text-red-600'}`}
                            >
                                <span className="uppercase">
                                    {formatAbsoluteGrowth(wowChange)}
                                </span>
                                &nbsp;{resolvedMetricLabelNoWrap}
                                {isPositive ? (
                                    <MoveUpRight className="size-3" />
                                ) : (
                                    <MoveDownRight className="size-3" />
                                )}
                            </span>
                        </div>
                    )}

                    {hasDominantSourceImpact && (
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-neutral-700">
                                {dominantSourceImpact.label}
                            </span>
                            <span
                                className={`text-sm font-semibold shrink-0 whitespace-nowrap ${dominantImpactPositive ? 'text-green-600' : 'text-red-600'}`}
                            >
                                <span className="uppercase">
                                    {formatAbsoluteGrowth(
                                        dominantSourceImpact.increase,
                                    )}
                                </span>
                                &nbsp;{dominantMetricLabelNoWrap}
                            </span>
                        </div>
                    )}
                </div>

                {shouldShowDriversSection && (
                    <>
                        <div className="h-px bg-neutral-200" />

                        {isLoading ? (
                            <div className="flex flex-col gap-1.5">
                                <div className="h-3 w-3/4 bg-neutral-200 rounded animate-pulse" />
                                <div className="flex gap-1">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <div
                                            key={i}
                                            className="w-6 h-8 bg-neutral-200 rounded-sm animate-pulse"
                                        />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-1.5">
                                {hashtags.length > 0 && (
                                    <div className="text-xs text-neutral-700 truncate">
                                        {hashtags.map((tag, i) => (
                                            <span key={tag}>
                                                {tag}
                                                {i < hashtags.length - 1
                                                    ? ', '
                                                    : ''}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {supportsPosts && posts.length > 0 && (
                                    <div className="flex gap-1">
                                        {posts.map((url, i) => (
                                            <MiniThumbnail
                                                key={i}
                                                url={url}
                                                dataSource={
                                                    normalizedDataSource
                                                }
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {(normalizedDataSource === 'google' ||
                            isLoading ||
                            hashtags.length > 0 ||
                            (supportsPosts && posts.length > 0)) && (
                            <div className="text-xs text-neutral-500 text-center">
                                Click to see details
                            </div>
                        )}
                    </>
                )}
            </div>
        </TikTokTooltipCard>
    );
}

export default DriversOfGrowthTooltip;
